import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import cn from 'classnames';

import BrButton from '@components/BrButton';
import BrIcons from '@components/BrIcons';
import BrOutsideAlerter from '@components/BrOutsideAlerter';
import BrSearchBox from '@components/BrSearchBox';
import BrText from '@components/BrText';
import ColorStyles from '@components/ColorStyles';
import './brDropdownMultiSelectMenu.scss';

export default class BrDropdownMultiSelectMenu extends PureComponent {
  static propTypes = {
    btnColorStyle: PropTypes.oneOf(Object.values(ColorStyles)),
    className: PropTypes.string,
    defaultTitle: PropTypes.string,
    doneBtnColorStyle: PropTypes.oneOf(Object.values(ColorStyles)), // color style specifically for the "Done" button
    isDisabled: PropTypes.bool,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.element,
        title: PropTypes.string,
        value: PropTypes.any,
        checked: PropTypes.bool,
        dom: PropTypes.node,
      }),
    ),
    onDone: PropTypes.func.isRequired,
    selectedItems: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.element,
        title: PropTypes.string,
        value: PropTypes.any,
        checked: PropTypes.bool,
        dom: PropTypes.node,
      }),
    ),
    allowSelectAllNone: PropTypes.bool,
    searchPlaceholderText: PropTypes.string,
    noItemFoundText: PropTypes.string,
    outsideClickDone: PropTypes.bool,
  };

  static defaultProps = {
    items: [],
    btnColorStyle: ColorStyles.GRAY,
    doneBtnColorStyle: ColorStyles.BGBLUE,
    isDisabled: false,
    defaultTitle: '',
    allowSelectAllNone: true,
    searchPlaceholderText: 'Search for an item...',
    noItemFoundText: 'No item matched your search',
    outsideClickDone: false,
  };

  constructor(props) {
    super(props);
    this.componentId = _.uniqueId('BrDropdownMultiSelectMenu-');
    this.state = {
      isActive: false,
      selectedItems: this.props.selectedItems || [],
      filteredItems: this.props.items || [],
    };
  }

  // eslint-disable-next-line camelcase
  UNSAFE_componentWillReceiveProps(nextProps) {
    const { selectedItems, items } = nextProps;
    if (selectedItems !== this.props.selectedItems) {
      this.setState({ selectedItems: nextProps.selectedItems || [] });
    }
    if (items !== this.props.items) {
      this.setState({ filteredItems: nextProps.items || [] });
    }
  }

  modifySelectedItems = (currentItem) => {
    let { selectedItems } = this.state;

    if (selectedItems.find((item) => item.value === currentItem.value)) {
      currentItem.checked = false;
      selectedItems = selectedItems.filter((item) => item.value !== currentItem.value);
    } else {
      currentItem.checked = true;
      selectedItems = [...selectedItems, currentItem];
    }
    this.setState({ selectedItems });
  };

  handleClickOpenBtn = () => {
    if (!this.props.isDisabled) {
      this.setState({ isActive: true });
    }
  };

  handleClickCloseBtn = () => {
    const { onDone, isDisabled } = this.props;
    if (!isDisabled) {
      this.setState({ isActive: false });
    }

    if (onDone) {
      onDone([...this.state.selectedItems]);
    }
  };

  handleClickOutside = () => {
    if (this.props.outsideClickDone) {
      this.handleClickCloseBtn();
      return;
    }

    this.setState({
      isActive: false,
      selectedItems: this.props.selectedItems || [],
    });
  };

  handleSelectAllOrNone = () => {
    const { selectedItems } = this.state;
    const { items } = this.props;
    if (selectedItems.length !== items.length) {
      this.setState({ selectedItems: items });
    } else {
      this.setState({ selectedItems: [] });
    }
  };

  handleQueryChange = (queryString) => {
    this.setState({
      filteredItems: this.props.items.filter((item) => item.title
        .toString()
        .toLowerCase()
        .includes(queryString.toString().toLowerCase())),
    });
  };

  renderDisplayTitle = (item, idx, toggleCheckboxCallback) => {
    const dropdownCheckboxID = `${this.componentId}dropdown-checkbox-${idx}`;
    return (
      <div className={cn('item-title', { disabled: this.props.isDisabled })}>
        {item.icon}
        <input
          className="item-select-checkbox"
          id={dropdownCheckboxID}
          type="checkbox"
          onClick={() => toggleCheckboxCallback(item)}
          checked={item.checked}
        />
        <label htmlFor={dropdownCheckboxID} className="label-item-checkbox">
          <BrText>{item.title || item.value}</BrText>
        </label>
        {item.dom}
      </div>
    );
  };

  render() {
    const {
      defaultTitle,
      items,
      className,
      btnColorStyle,
      doneBtnColorStyle,
      isDisabled,
      allowSelectAllNone,
      searchPlaceholderText,
      noItemFoundText,
      outsideClickDone,
    } = this.props;
    const { isActive, selectedItems, filteredItems } = this.state;
    const classname = cn('br-multi-select-dropdown-menu', className, { active: isActive, disabled: isDisabled });

    let btnTitle = selectedItems ? `${selectedItems.length} selected` : defaultTitle || '0 selected';
    btnTitle = (
      <div className="multi-select-dropdown-title" title={btnTitle}>
        <div className={cn('item-title', isDisabled && 'disabled')}>{btnTitle}</div>
        <BrIcons.DownCaret isSubComp />
      </div>
    );

    const optionItems = allowSelectAllNone || !outsideClickDone ? (
      <div className="options-done-container">
        {allowSelectAllNone && (
          <div className="select-options-container">
            <input
              type="checkbox"
              className="select-all-none-checkbox"
              id={`${this.componentId}select-all-none-checkbox`}
              onClick={this.handleSelectAllOrNone}
              checked={selectedItems.length === items.length}
              ref={(el) => {
                if (el) {
                  el.indeterminate = selectedItems.length > 0 && selectedItems.length !== items.length;
                }
              }}
            />
            <label
              htmlFor={`${this.componentId}select-all-none-checkbox`}
              className="select-all-none-label"
            >
              Select all/none
            </label>
          </div>
        )}
        {!outsideClickDone && (
          <div className="done-button-container">
            <BrButton
              title="Done"
              onClick={this.handleClickCloseBtn}
              colorStyle={doneBtnColorStyle}
            />
          </div>
        )}
      </div>
    ) : null;

    let finalItems = [...filteredItems];
    finalItems = finalItems.map((item) => {
      item.checked = Object.keys(selectedItems).length > 0
        && selectedItems.find((currentItem) => item.value === currentItem.value) !== undefined;
      return item;
    });

    let dropdownItems;
    if (finalItems.length > 0) {
      dropdownItems = finalItems.map((item, idx) => (
        <div key={JSON.stringify(idx)} className="dropdown-item" title={item.title || item.value}>
          {this.renderDisplayTitle(item, idx, this.modifySelectedItems)}
        </div>
      ));
    } else {
      dropdownItems = <div className="no-dropdown-item">{noItemFoundText}</div>;
    }

    return (
      <BrOutsideAlerter className={className} callbackFunc={this.handleClickOutside}>
        <div className={classname} tabIndex="0" data-id="br-multi-select-dropdown-menu">
          <BrButton title={btnTitle} onClick={this.handleClickOpenBtn} isSubComp colorStyle={btnColorStyle} />
          <div className="multi-select-dropdown-item-list">
            {optionItems}
            <BrSearchBox
              className="item-search-box"
              placeholder={searchPlaceholderText}
              onQueryChange={this.handleQueryChange}
              onQuerySubmit={this.handleQueryChange}
            />
            <div className="dropdown-item-list">{dropdownItems}</div>
          </div>
        </div>
      </BrOutsideAlerter>
    );
  }
}
